/**
 * Reseller GHL connection tab (TMMPDM-0015; SASATH-0339 §2.5). `getResellerGhl` → `null`
 * (404) renders "Not connected" + Connect…; a connection renders location id, the masked
 * token (`••••` + the server's suffix) when `tokenSet`, the active state and updated-at, with
 * Edit… / Disconnect….
 *
 * The private integration token is write-only: the edit form's field is `type="password"`,
 * empty by default, and sent only when non-blank (blank keeps the stored one). It lives only
 * in the form component's state — cleared once the submit settles (kept only across a 422
 * `GHL_LOCATION_NOT_VERIFIED`, TMMPDM-0024) and destroyed with the form on close — and is never
 * rendered back, logged, or put in a URL. 409 `GHL_LOCATION_ALREADY_CLAIMED` and 422
 * `GHL_LOCATION_NOT_VERIFIED` → a field error on location id.
 *
 * TMMPDM-0017: tmm-api `/v1/admin/resellers/{id}/ghl-integration` (saas-auth's DTO passed
 * through). A 502 `SAAS_AUTH_OUTCOME_UNKNOWN` on a write re-reads the connection and says so;
 * nothing is re-sent.
 *
 * TMMPDM-0018: an active connection offers Diagnose (`POST …/ghl-integration/diagnose`, 25 s
 * timeout): the server's `interpretation` as the headline — shown as-is, never re-derived from
 * the two probes — then the Contacts and Custom fields probe rows. The token is never involved.
 *
 * TMMPDM-0025 (tmm-api 0087): the save proves control with GHL (up to 30 s server-side; 35 s client
 * timeout) — Save shows progress and says so; the 422 text is tmm-api's recommended copy.
 */
import { useEffect, useState } from 'react';
import { useDiscardChangesGuard, type DiscardChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  SectionCard,
  SubmitButton,
  useNotify,
} from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AppSwitch } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import {
  ApiError,
  errorCopy,
  isOutcomeUnknown,
  isValidationFailed,
  validationFields,
} from '@/api/errors';
import type {
  GhlProbeOutcomeDto,
  ResellerGhlIntegrationDiagnosisDto,
  ResellerGhlIntegrationDto,
  UpsertResellerGhlRequest,
} from '@/api/tmm/types';
import { formatInstant } from '@/format';

interface ResellerGhlPanelProps {
  resellerId: number;
}

/** The server's suffix already leads with "…" (Java `maskedSuffix`); the mask supplies its own dots. */
function maskedToken(suffix: string | null): string {
  return `••••${(suffix ?? '').replace(/^…/, '')}`;
}

export function ResellerGhlPanel({ resellerId }: ResellerGhlPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${resellerId}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    ghl: ResellerGhlIntegrationDto | null;
    failure: unknown;
  }>({ requestKey: null, ghl: null, failure: null });
  const [editOpen, setEditOpen] = useState(false);
  const discard = useDiscardChangesGuard(
    t('resellers.ghl.connectionName', 'the GHL connection'),
    () => setEditOpen(false),
  );
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState<{
    requestKey: string;
    result: ResellerGhlIntegrationDiagnosisDto;
    /** When the answer arrived: the reference support gets (a success carries no request id). */
    checkedAt: string;
  } | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  // A refused Disconnect is said inside its confirm, which stays open (TMM-B4).
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  // Warnings and errors stay a banner; quiet success is a toast (TMMPDM-0034).
  const notify = useNotify();
  const [notice, setNotice] = useState<{
    severity: 'warning' | 'error';
    message: string;
  } | null>(null);

  const reloadAfterUnknownOutcome = () => {
    setNotice({
      severity: 'warning',
      message: t(
        'common.outcomeUnknown',
        "We couldn't confirm the change. The screen has been reloaded — check before trying again.",
      ),
    });
    setReloadToken((n) => n + 1);
  };

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getResellerGhl(resellerId)
      .then((ghl) => {
        if (!cancelled) setResponse({ requestKey, ghl, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResponse({ requestKey, ghl: null, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, requestKey]);

  const current = response.requestKey === requestKey;
  const ghl = current ? response.ghl : null;
  const lastDiagnosis = diagnosis?.requestKey === requestKey ? diagnosis : null;

  const handleDiagnose = async () => {
    setNotice(null);
    setDiagnosis(null);
    setDiagnosing(true);
    try {
      const result = await adminApi.diagnoseResellerGhl(resellerId);
      setDiagnosis({ requestKey, result, checkedAt: new Date().toISOString() });
    } catch (err) {
      setNotice({
        severity: 'error',
        message: errorCopy(
          err,
          t,
          t('resellers.ghl.diagnose.error', 'Could not run the diagnosis.'),
        ),
      });
    } finally {
      setDiagnosing(false);
    }
  };

  const handleDisconnect = async () => {
    setNotice(null);
    setDisconnectError(null);
    setDisconnecting(true);
    try {
      await adminApi.deleteResellerGhl(resellerId);
      setDisconnectOpen(false);
      notify.success(t('resellers.ghl.disconnected', 'Disconnected.'));
      setReloadToken((n) => n + 1);
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        setDisconnectOpen(false);
        reloadAfterUnknownOutcome();
        return;
      }
      setDisconnectError(
        errorCopy(err, t, t('resellers.ghl.disconnectError', 'Could not disconnect.')),
      );
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        {t('resellers.ghl.title', 'GHL connection')}
      </Typography>

      {notice ? (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      ) : null}

      {!current ? <LoadingSpinner /> : null}
      {current && response.failure ? (
        <ErrorState
          error={response.failure}
          message={t('resellers.ghl.loadError', 'Could not load the GHL connection.')}
          onRetry={() => setReloadToken((n) => n + 1)}
        />
      ) : null}

      {current && !response.failure && ghl === null ? (
        <EmptyState
          surface
          title={t('resellers.ghl.notConnected', 'Not connected')}
          action={
            <Button variant="contained" onClick={() => setEditOpen(true)}>
              {t('resellers.ghl.connect', 'Connect…')}
            </Button>
          }
        />
      ) : null}

      {ghl ? (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 560, mb: 2 }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {t('resellers.ghl.locationId', 'Location id')}
                  </TableCell>
                  <TableCell>{ghl.locationId ?? '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {t('resellers.ghl.token', 'Private integration token')}
                  </TableCell>
                  <TableCell>
                    {ghl.tokenSet
                      ? maskedToken(ghl.tokenSuffix)
                      : t('resellers.ghl.tokenNotSet', 'Not set')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {t('resellers.ghl.active', 'Active')}
                  </TableCell>
                  <TableCell>
                    {ghl.active ? t('resellers.ghl.yes', 'Yes') : t('resellers.ghl.no', 'No')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {t('resellers.overview.updated', 'Updated')}
                  </TableCell>
                  <TableCell>{formatInstant(ghl.updatedAt, i18n.language)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={1}>
            {ghl.active ? (
              <Button
                variant="contained"
                onClick={() => void handleDiagnose()}
                disabled={diagnosing}
              >
                {t('resellers.ghl.diagnose.button', 'Diagnose')}
              </Button>
            ) : null}
            <Button variant="outlined" onClick={() => setEditOpen(true)}>
              {t('resellers.ghl.edit', 'Edit…')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setDisconnectError(null);
                setDisconnectOpen(true);
              }}
            >
              {t('resellers.ghl.disconnect', 'Disconnect…')}
            </Button>
          </Stack>
          {diagnosing ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2 }} role="status">
              <CircularProgress
                size={20}
                aria-label={t('resellers.ghl.diagnose.running', 'Running the diagnosis')}
              />
              <Typography variant="body2">
                {t('resellers.ghl.diagnose.wait', 'Checking GHL — this can take a few seconds.')}
              </Typography>
            </Stack>
          ) : null}
          {lastDiagnosis ? (
            <DiagnosisCard diagnosis={lastDiagnosis.result} checkedAt={lastDiagnosis.checkedAt} />
          ) : null}
        </>
      ) : null}

      <Dialog open={editOpen} onClose={discard.requestClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {ghl
            ? t('resellers.ghl.editTitle', 'Edit GHL connection')
            : t('resellers.ghl.connectTitle', 'Connect GHL')}
        </DialogTitle>
        {editOpen ? (
          <GhlForm
            resellerId={resellerId}
            existing={ghl}
            discard={discard}
            onSaved={() => {
              setEditOpen(false);
              setNotice(null);
              notify.success(t('resellers.ghl.saved', 'GHL connection saved.'));
              setReloadToken((n) => n + 1);
            }}
            onOutcomeUnknown={() => {
              setEditOpen(false);
              reloadAfterUnknownOutcome();
            }}
          />
        ) : null}
      </Dialog>
      {discard.dialog}

      <ConfirmDialog
        open={disconnectOpen}
        title={t('resellers.ghl.disconnectTitle', 'Disconnect GHL?')}
        consequence={t(
          'resellers.ghl.disconnectBody',
          'This deletes the reseller’s GHL connection and its stored token.',
        )}
        confirmLabel={t('resellers.ghl.disconnectConfirm', 'Disconnect')}
        destructive
        busy={disconnecting}
        error={disconnectError}
        onConfirm={() => void handleDisconnect()}
        onCancel={() => setDisconnectOpen(false)}
      />
    </Box>
  );
}

type Severity = 'success' | 'warning' | 'info';

/** The server's interpretation → a severity, a headline and a one-line hint. */
const INTERPRETATIONS: Record<string, { severity: Severity; label: string; hint: string }> = {
  HEALTHY: { severity: 'success', label: 'Healthy', hint: 'Both checks passed.' },
  LIKELY_MISSING_SCOPE: {
    severity: 'warning',
    label: 'Missing permission',
    hint: 'The token is missing a permission GHL needs.',
  },
  LIKELY_CREDENTIAL_CORRUPT: {
    severity: 'warning',
    label: 'Token damaged',
    hint: 'The stored token looks damaged — set it again.',
  },
  LIKELY_CREDENTIAL_INVALID: {
    severity: 'warning',
    label: 'Token rejected',
    hint: 'GHL rejected the token — replace it.',
  },
  UPSTREAM_UNAVAILABLE: {
    severity: 'info',
    label: 'GHL unavailable',
    hint: "GHL didn't answer — try again later.",
  },
  INDETERMINATE: {
    severity: 'info',
    label: 'No clear pattern',
    hint: "The checks didn't match a known pattern — see the results below.",
  },
};

/**
 * What support needs from a diagnosis: the verdict and each probe's raw status, kind and GHL's own
 * message. GHL's text is third-party text, not a documented user-facing SDK message, so it is never
 * shown (TMM-B3); it is copied, with the reseller, the location and when it was checked.
 */
function ghlSupportDetails(
  diagnosis: ResellerGhlIntegrationDiagnosisDto,
  checkedAt: string,
): string {
  const value = (v: string | number | null) => (v === null || v === '' ? '-' : String(v));
  const probe = (name: string, outcome: GhlProbeOutcomeDto) =>
    `${name}: ok=${outcome.ok} status=${value(outcome.status)} kind=${value(outcome.kind)} message=${value(outcome.upstreamMessage)}`;
  return [
    `resellerId: ${diagnosis.resellerId}`,
    `locationId: ${value(diagnosis.locationId)}`,
    `checkedAt: ${checkedAt}`,
    `interpretation: ${String(diagnosis.interpretation)}`,
    probe('contacts', diagnosis.contacts),
    probe('customFields', diagnosis.customFields),
  ].join('\n');
}

/**
 * The server's verdict as catalog copy. An interpretation this console doesn't know says so in one
 * line; the raw code, `kind`, status and GHL's message are never displayed, only copied for support.
 */
function DiagnosisCard({
  diagnosis,
  checkedAt,
}: {
  diagnosis: ResellerGhlIntegrationDiagnosisDto;
  checkedAt: string;
}) {
  const { t } = useCopy();
  const notify = useNotify();
  const code = String(diagnosis.interpretation);
  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(ghlSupportDetails(diagnosis, checkedAt));
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };
  const known = INTERPRETATIONS[code];
  const probes: Array<{ key: string; label: string; probe: GhlProbeOutcomeDto }> = [
    {
      key: 'contacts',
      label: t('resellers.ghl.diagnose.contacts', 'Contacts'),
      probe: diagnosis.contacts,
    },
    {
      key: 'customFields',
      label: t('resellers.ghl.diagnose.customFields', 'Custom fields'),
      probe: diagnosis.customFields,
    },
  ];
  return (
    <SectionCard
      headingLevel="h3"
      title={t('resellers.ghl.diagnose.title', 'Diagnosis')}
      id="ghl-diagnosis-title"
    >
      <Alert severity={known?.severity ?? 'info'} sx={{ mb: 1 }}>
        <AlertTitle>
          {known
            ? t(`resellers.ghl.diagnose.interpretation.${code}`, known.label)
            : t(
                'resellers.ghl.diagnose.unknown',
                "GoHighLevel returned a result this console doesn't recognise.",
              )}
        </AlertTitle>
        {known ? t(`resellers.ghl.diagnose.hint.${code}`, known.hint) : null}
      </Alert>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('resellers.ghl.diagnose.title', 'Diagnosis')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('resellers.ghl.diagnose.check', 'Check')}</TableCell>
              <TableCell>{t('resellers.ghl.diagnose.result', 'Result')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {probes.map(({ key, label, probe }) => (
              <TableRow key={key}>
                <TableCell component="th" scope="row">
                  {label}
                </TableCell>
                <TableCell>
                  {probe.ok
                    ? t('resellers.ghl.diagnose.ok', 'OK')
                    : t('resellers.ghl.diagnose.failed', 'Failed')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => void copyDetails()}>
        {t('resellers.ghl.diagnose.copyDetails', 'Copy details for support')}
      </Button>
    </SectionCard>
  );
}

interface GhlFormProps {
  resellerId: number;
  existing: ResellerGhlIntegrationDto | null;
  /** Esc, the backdrop and Cancel ask first when the form is dirty (TMM-B5). */
  discard: DiscardChangesGuard;
  onSaved: () => void;
  onOutcomeUnknown: () => void;
}

/**
 * saas-auth `UpsertGhlIntegrationRequest.locationId` (`@Pattern("\\s*|[A-Za-z0-9_-]{1,64}")`,
 * SASATH-0354 §2.2), anchored: blank for no location, else 1–64 letters, digits, `_` or `-`.
 */
const LOCATION_ID_RULE = /^\s*$|^[A-Za-z0-9_-]{1,64}$/;

/**
 * Mounted only while the dialog is open, so the token state dies with it on close.
 *
 * TMMPDM-0024: the location id is checked against saas-auth's rule as typed and on submit. A new
 * or changed location (or a new token) is verified with GHL first: 422 `GHL_LOCATION_NOT_VERIFIED`
 * means nothing was saved — shown on the location field, and the token stays in state so it can be
 * corrected without retyping. Every other outcome clears the token.
 */
function GhlForm({ resellerId, existing, discard, onSaved, onOutcomeUnknown }: GhlFormProps) {
  const { t } = useCopy();
  const [locationId, setLocationId] = useState(existing?.locationId ?? '');
  const [token, setToken] = useState('');
  const [active, setActive] = useState(existing?.active ?? true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const dirty =
    locationId !== (existing?.locationId ?? '') ||
    token !== '' ||
    active !== (existing?.active ?? true);
  const { setDirty } = discard;
  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);
  const locationRule = t(
    'resellers.ghl.locationRule',
    "1–64 letters, digits, '_' or '-'. Leave blank for no location.",
  );
  const locationValid = LOCATION_ID_RULE.test(locationId);

  const handleSubmit = async () => {
    if (!locationValid) return;
    const body: UpsertResellerGhlRequest = { active };
    if (locationId.trim() !== '') body.locationId = locationId.trim();
    if (token.trim() !== '') body.privateIntegrationToken = token.trim();
    setFieldErrors({});
    setFormError(null);
    setSubmitting(true);
    try {
      await adminApi.upsertResellerGhl(resellerId, body);
      setToken('');
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'GHL_LOCATION_NOT_VERIFIED') {
        // Nothing was saved; the token is kept (never rendered — the input is a password field).
        setFieldErrors({
          locationId: t(
            'resellers.ghl.locationNotVerified',
            "This token can't read that location — check the location id and the token's scopes.",
          ),
        });
        return;
      }
      setToken('');
      if (isOutcomeUnknown(err)) {
        onOutcomeUnknown();
      } else if (err instanceof ApiError && err.code === 'GHL_LOCATION_ALREADY_CLAIMED') {
        setFieldErrors({
          locationId: t(
            'resellers.ghl.locationClaimed',
            'That location is already connected elsewhere.',
          ),
        });
      } else if (isValidationFailed(err)) {
        // A refusal that marks no field this form shows still says something: the form's own line.
        const marked = validationFields(err, t);
        if (marked.locationId !== undefined || marked.privateIntegrationToken !== undefined) {
          setFieldErrors(marked);
        } else {
          setFormError(t('resellers.ghl.saveError', 'Could not save the connection.'));
        }
      } else {
        setFormError(
          errorCopy(err, t, t('resellers.ghl.saveError', 'Could not save the connection.')),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <TextField
            label={t('resellers.ghl.locationId', 'Location id')}
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            error={!locationValid || Boolean(fieldErrors.locationId)}
            helperText={locationValid ? (fieldErrors.locationId ?? locationRule) : locationRule}
            size="small"
            fullWidth
          />
          <TextField
            label={t('resellers.ghl.token', 'Private integration token')}
            type="password"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            error={Boolean(fieldErrors.privateIntegrationToken)}
            helperText={
              fieldErrors.privateIntegrationToken ??
              t('resellers.ghl.tokenHelper', 'Leave blank to keep the current token')
            }
            size="small"
            fullWidth
          />
          <AppSwitch
            label={t('resellers.ghl.active', 'Active')}
            checked={active}
            onChange={setActive}
          />
          {submitting ? (
            <Typography variant="body2" role="status">
              {t(
                'resellers.ghl.saveWait',
                'Checking the token with GHL — this can take up to 30 seconds.',
              )}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={discard.requestClose} disabled={submitting}>
          {t('resellers.cancel', 'Cancel')}
        </Button>
        <SubmitButton
          onClick={() => void handleSubmit()}
          busy={submitting}
          disabled={!locationValid}
        >
          {t('resellers.save', 'Save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
